import { useMemo, useState } from "react";

import LoadingSvg2 from "./LoadingSvg2";
import T, { GetLang } from "./translations/Translations";
import NewUserStartForm from "./NewUserStartForm";
import Toolbar from "@components/Toolbar";
import WelcomeHeader from "./header/WelcomeHeader";
import WelcomeAccordions from "./WelcomeAccordions";
import { useGlobalContext } from "./contextProvider/ContextProvider";
import { useOpenEnrollmentData } from "@hooks/useOpenEnrollmentData";
import { useOpenEnrollmentWindows } from "@hooks/useOpenEnrollmentWindows";

import "@/styles/WelcomeText.scss";

/**
 * For external guardians, determine what instructions to display based on the appropriate OE windows
 * @name WelcomeText
 * @returns {JSX.Element}
 */
const WelcomeToOpenEnrollment = (): JSX.Element => {
    const { state } = useGlobalContext();
    const { oeActionControls, oeDistrictWindows } = state || {};
    const lang = GetLang();

    // Custom hooks for logic extraction
    const { loading } = useOpenEnrollmentData();
    const { intentToReturn, parentOffer, parentRequest, parentRequestRound2, windowsReady } = useOpenEnrollmentWindows(
        oeActionControls,
        oeDistrictWindows
    );

    // Accordion state management
    const initialActiveKeys = useMemo(() => ["0", "1", "2", "3", "4", "5"], []);
    const [activeKeys, setActiveKeys] = useState(initialActiveKeys);

    const handleAccordionSelect = (key: string | string[] | null | undefined): void => {
        if (Array.isArray(key)) {
            setActiveKeys(key);
        } else {
            setActiveKeys(key ? [key] : []);
        }
    };

    ///////////////////////////////////////////////////////////////////////////
    // Translations
    // declared here to prevent rule of hooks violations
    //-------------------------------------------------------------------------
    const welcomeTagline = T({ tKey: "Welcome_Tagline", langStr: lang });
    const welcomeP1 = T({ tKey: "Welcome_p1", langStr: lang });
    const welcomeP2 = T({ tKey: "Welcome_p2", langStr: lang });
    const welcomeH51 = T({ tKey: "Welcome_h5_1", langStr: lang });
    const oeToolbar = <>DCSD {T({ tKey: "OEApp_Header", langStr: lang })}</>;

    return (
        <div style={{ display: "flex", flexDirection: "column" }}>
            <WelcomeHeader />
            <Toolbar icon="OE" label={oeToolbar} />
            <div className="gutter-96">
                <div className="welcome-text-container">
                    <h4>{welcomeTagline}</h4>
                    {welcomeP1}
                    {welcomeP2}
                    {welcomeH51}
                    <br />
                    <WelcomeAccordions
                        activeKeys={activeKeys}
                        handleAccordionSelect={handleAccordionSelect}
                        intentToReturn={intentToReturn}
                        oeDistrictWindows={oeDistrictWindows}
                        oeWindowsFlag={windowsReady}
                        parentOffer={parentOffer}
                        parentRequest={parentRequest}
                        parentRequestRound2={parentRequestRound2}
                        showBeforeYouBegin={true}
                    />
                </div>
                <NewUserStartForm />
                <div className="info-footer mt-5">{T({ tKey: "Welcome_InfoFooter", langStr: lang })}</div>
                <div className="spacer" />
            </div>
            {loading && <LoadingSvg2 />}
        </div>
    );
};

export default WelcomeToOpenEnrollment;
